/**
 * Pagination Component
 *
 * @module shared/components/data-table/Pagination
 *
 * Reusable pagination controls for tables and lists.
 * Provides page navigation, per-page selection, and item count display.
 */

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';

// ============================================================================
// Types
// ============================================================================

export interface PaginationProps {
  /**
   * Total number of items across all pages
   */
  totalItems: number;
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;
  /**
   * Number of items per page
   */
  perPage: number;
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Callback when items per page changes
   */
  onPerPageChange: (perPage: number) => void;
  /**
   * Available options for items per page
   * @default [10, 20, 50, 100]
   */
  perPageOptions?: number[];
  /**
   * Label for the per page selector
   * @default 'Rows per page:'
   */
  perPageLabel?: string;
  /**
   * Whether to show the items info (e.g., "Showing 1-20 of 100")
   * @default true
   */
  showItemsInfo?: boolean;
  /**
   * Whether to show first/last page buttons
   * @default true
   */
  showFirstLastButtons?: boolean;
  /**
   * Whether the pagination is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium';
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PER_PAGE_OPTIONS = [10, 20, 50, 100];

// ============================================================================
// Component
// ============================================================================

export function Pagination({
  totalItems,
  currentPage,
  perPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  perPageLabel = 'Rows per page:',
  showItemsInfo = true,
  showFirstLastButtons = true,
  disabled = false,
  size = 'medium',
}: PaginationProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculations
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalItems);

  // Navigation helpers
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handleFirstPage = () => {
    if (!disabled && canGoPrevious) {
      onPageChange(1);
    }
  };

  const handlePreviousPage = () => {
    if (!disabled && canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (!disabled && canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (!disabled && canGoNext) {
      onPageChange(totalPages);
    }
  };

  const handlePerPageChange = (value: number) => {
    if (!disabled) {
      onPerPageChange(value);
    }
  };

  // Size-based styles
  const isSmall = size === 'small' || isMobile;
  const textVariant = isSmall ? 'caption' : 'body2';
  const iconSize = isSmall ? 'small' : 'medium';
  const buttonSize = isSmall ? 'small' : 'medium';
  const effectiveShowFirstLast = showFirstLastButtons && !isMobile;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 2,
        px: isSmall ? 2 : 3,
        py: isSmall ? 1.5 : 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left side: Items info */}
      {showItemsInfo && (
        <Typography
          variant={textVariant}
          color="text.secondary"
          sx={{ minWidth: 'fit-content', ...(isMobile && { textAlign: 'center' }) }}
        >
          {totalItems === 0
            ? 'No items'
            : `Showing ${startItem} - ${endItem} of ${totalItems} items`}
        </Typography>
      )}

      {/* Spacer when no items info */}
      {!showItemsInfo && !isMobile && <Box />}

      {/* Right side: Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isSmall ? 1.5 : 2,
          ...(isMobile && { justifyContent: 'space-between', width: '100%' }),
        }}
      >
        {/* Per page selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isMobile && (
            <Typography variant={textVariant} color="text.secondary">
              {perPageLabel}
            </Typography>
          )}
          <FormControl size="small" disabled={disabled}>
            <Select
              value={perPage}
              onChange={(e) => handlePerPageChange(Number(e.target.value))}
              sx={{
                minWidth: isSmall ? 60 : 70,
                '& .MuiSelect-select': {
                  py: isSmall ? 0.25 : 0.5,
                  fontSize: isSmall ? '0.75rem' : '0.875rem',
                },
              }}
            >
              {perPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Page navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {effectiveShowFirstLast && (
            <IconButton
              size={buttonSize}
              onClick={handleFirstPage}
              disabled={disabled || !canGoPrevious}
              aria-label="Go to first page"
            >
              <FirstPageIcon fontSize={iconSize} />
            </IconButton>
          )}
          <IconButton
            size={buttonSize}
            onClick={handlePreviousPage}
            disabled={disabled || !canGoPrevious}
            aria-label="Go to previous page"
          >
            <ChevronLeftIcon fontSize={iconSize} />
          </IconButton>

          <Typography
            variant={textVariant}
            sx={{
              mx: 1,
              minWidth: 'fit-content',
              userSelect: 'none',
            }}
          >
            {isMobile ? `${currentPage} / ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
          </Typography>

          <IconButton
            size={buttonSize}
            onClick={handleNextPage}
            disabled={disabled || !canGoNext}
            aria-label="Go to next page"
          >
            <ChevronRightIcon fontSize={iconSize} />
          </IconButton>
          {effectiveShowFirstLast && (
            <IconButton
              size={buttonSize}
              onClick={handleLastPage}
              disabled={disabled || !canGoNext}
              aria-label="Go to last page"
            >
              <LastPageIcon fontSize={iconSize} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}


